import { useContext, useEffect, useState } from 'react';
import { FaRobot, FaDownload } from 'react-icons/fa';
import { AuthContext } from '../../../context/AuthContext';
import usageReportsApi from '../../../services/usageReportsApi';

const EVENT_TYPE_LABELS = {
  login: 'Logged in',
  ai_health_report_generated: 'Generated an AI health report',
  ai_chat_query: 'Used the AI chat assistant',
  account_created: 'Account created',
  consultation_booked: 'Booked a consultation',
  feedback_submitted: 'Submitted feedback',
  production_record_logged: 'Logged a production record',
};

function label(eventType) {
  return EVENT_TYPE_LABELS[eventType] || eventType;
}

function fmtDate(d) {
  return new Date(d).toLocaleString();
}

// A real, self-service record of the customer's own AI and platform usage
// — pulled directly from the same UsageEvent log the admin reports use, so
// what a customer sees here always matches what admin sees for them.
export default function AiActivityHistory() {
  const { user } = useContext(AuthContext);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await usageReportsApi.getMyHistory();
        if (mounted) setHistory(res);
      } catch (err) {
        /* interceptor toasts */
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const handleDownloadPdf = () => {
    window.print();
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64 text-gray-500">Loading your activity…</div>;
  }

  const events = history?.events || [];
  const aiEvents = events.filter((e) => e.isAiFeature);

  return (
    <div className="p-4 md:p-6 space-y-6 print:p-0">
      <style>{`
        @media print {
          nav, aside, header button, .no-print { display: none !important; }
          body { background: white; }
        }
      `}</style>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800">My AI &amp; Platform Activity</h1>
          <p className="text-xs text-gray-400 mt-1">
            {user?.firstName ? `${user.firstName}'s` : 'Your'} real, timestamped usage history — recorded automatically as you use the platform.
          </p>
        </div>
        <button
          onClick={handleDownloadPdf}
          className="no-print flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-primary text-white hover:opacity-90"
        >
          <FaDownload /> Download as PDF
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <div className="text-xs text-gray-400">Total recorded activity</div>
          <div className="text-2xl font-bold text-gray-800">{events.length}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <div className="text-xs text-gray-400">AI feature uses</div>
          <div className="text-2xl font-bold text-gray-800">{aiEvents.length}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <div className="text-xs text-gray-400">Most recent activity</div>
          <div className="text-sm font-medium text-gray-700">{events[0] ? fmtDate(events[0].createdAt) : '—'}</div>
        </div>
      </div>

      {events.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100 text-center">
          <FaRobot className="mx-auto text-3xl text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">
            No activity has been recorded yet. As you log in and use AI features, they'll appear here automatically.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-400 border-b border-gray-100">
                <th className="py-3 px-4">When</th>
                <th className="py-3 px-4">Activity</th>
                <th className="py-3 px-4">AI feature?</th>
                <th className="py-3 px-4">Response time</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e._id} className="border-b border-gray-50">
                  <td className="py-2 px-4 whitespace-nowrap text-gray-500">{fmtDate(e.createdAt)}</td>
                  <td className="py-2 px-4 text-gray-700">{label(e.eventType)}</td>
                  <td className="py-2 px-4">{e.isAiFeature ? 'Yes' : 'No'}</td>
                  <td className="py-2 px-4">{e.durationMs != null ? `${e.durationMs} ms` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-400">
        This history reflects only activity recorded since usage logging was enabled on this platform. It cannot include earlier usage.
      </p>
    </div>
  );
}
